// src/datasources/AddDataSourceDialog.tsx
import * as React from "react";
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Stack,
  LinearProgress,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Typography,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  AddToolConfigInput,
  CreateCsvUploadUrlInput,
  DataSource,
  AddDataSourceInput,
  User,
  LoadCsvToRedshiftInput,
  ToolConfig,
  ListToolsInput,
} from "../../models/API";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import {
  addToolConfig,
  createCsvUploadUrl,
  addDataSource,
  loadCsvToRedshift,
} from "../../graphql/mutations";
import { listTools } from "../../graphql/queries";
import { generateClient } from "aws-amplify/api";
import FullSchemaEditor from "./FullSchemaEditor";
import {
  isValidRedshiftColumnName,
  REDSHIFT_RESERVED_KEYWORDS,
} from "../utils/utils";

type Mode = "add" | "edit";

// Sentinel value for "Create new tool" option
const CREATE_NEW_TOOL_ID = -1;

type Props = {
  open: boolean;
  mode?: Mode;
  currentUserId: number;
  currentTeamId: number;
  initial?: Partial<DataSource>; // when editing, pass the row
  userConfig: User;
  effectiveRole: string;
  onClose: () => void;
  onCreate?: (
    ds: Omit<DataSource, "id" | "createdAt" | "status"> & { blob?: File }
  ) => Promise<void> | void;
  onUpdate?: (
    id: string,
    ds: Partial<DataSource> & { blob?: File | null }
  ) => Promise<void> | void;
};

export default function AddDataSourceDialog({
  open,
  mode = "add",
  currentUserId,
  currentTeamId,
  initial,
  userConfig,
  effectiveRole,
  onClose,
}: Props) {
  const isEdit = mode === "edit";
  const client = React.useMemo(() => generateClient(), []);

  // Tool list state
  const [tools, setTools] = React.useState<ToolConfig[]>([]);
  const [loadingTools, setLoadingTools] = React.useState(false);

  // Selected tool state
  const [selectedToolId, setSelectedToolId] = React.useState<number | null>(
    initial?.tool_id ?? null
  );

  // "Create new tool" fields (FINN admin only)
  const [newToolName, setNewToolName] = React.useState("");
  const [newToolAccessor, setNewToolAccessor] = React.useState("");
  const [newToolDescription, setNewToolDescription] = React.useState("");
  const [newToolInputSchema, setNewToolInputSchema] = React.useState<any>({});
  const [newToolCategory, setNewToolCategory] = React.useState("data");
  const [newToolType, setNewToolType] = React.useState("DB");

  // Data source fields
  const [name, setName] = React.useState(initial?.display_name ?? "");
  const [description, setDescription] = React.useState(
    initial?.description ?? ""
  );
  const [isPublic, setIsPublic] = React.useState<boolean>(
    initial?.is_public ?? false
  );
  const [isGlobal, setIsGlobal] = React.useState<boolean>(
    initial?.is_global ?? false
  );

  // CSV state
  const [csvFile, setCsvFile] = React.useState<File | null>(null);
  const [csvPreview, setCsvPreview] = React.useState<string[][] | null>(null);

  // UI state
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Permission check: can this user create new tools?
  const canCreateTool = effectiveRole !== "user" && currentTeamId === 1;

  // Derived: selected tool object
  const selectedTool = React.useMemo(
    () => tools.find((t) => t.id === selectedToolId) ?? null,
    [tools, selectedToolId]
  );

  // Derived: data_source_type from selected tool
  const derivedDataSourceType = React.useMemo(() => {
    if (selectedToolId === CREATE_NEW_TOOL_ID) {
      return newToolType;
    }
    return selectedTool?.tool_type ?? "DB";
  }, [selectedTool, selectedToolId, newToolType]);

  // Fetch tools on dialog open
  React.useEffect(() => {
    if (!open) return;
    const fetchTools = async () => {
      setLoadingTools(true);
      try {
        const input: ListToolsInput = {
          tool_category: "data",
        };
        const result = await client.graphql({
          query: listTools,
          variables: { input },
        });
        setTools(result.data.listTools || []);
      } catch (e: any) {
        console.error("Failed to fetch tools:", e);
      } finally {
        setLoadingTools(false);
      }
    };
    fetchTools();
  }, [open, client]);

  // Reset initial values when initial prop changes or tools load
  React.useEffect(() => {
    setName(initial?.display_name ?? "");
    setDescription(initial?.description ?? "");
    setIsPublic(initial?.is_public ?? false);
    setIsGlobal(initial?.is_global ?? false);
    // Set selectedToolId from initial.tool_id once tools are loaded
    if (initial?.tool_id && tools.length > 0) {
      setSelectedToolId(initial.tool_id);
    } else if (!initial?.tool_id) {
      setSelectedToolId(null);
    }
  }, [initial, tools]);

  // CSV preview
  React.useEffect(() => {
    if (!csvFile) {
      setCsvPreview(null);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || "");
      const lines = text.split(/\r?\n/).slice(0, 5);
      const cells = lines.map((l) => l.split(",").slice(0, 100));
      setCsvPreview(cells);
    };
    reader.readAsText(csvFile.slice(0, 4096));
  }, [csvFile]);

  const reset = () => {
    setName("");
    setDescription("");
    setIsPublic(false);
    setSelectedToolId(null);
    setNewToolName("");
    setNewToolAccessor("");
    setNewToolDescription("");
    setNewToolInputSchema({});
    setNewToolCategory("data");
    setNewToolType("DB");
    setCsvFile(null);
    setCsvPreview(null);
    setSubmitting(false);
    setError(null);
  };

  const validate = (): string | null => {
    if (!name.trim()) return "Please enter a data source name.";
    if (selectedToolId === null) return "Please select a tool.";
    if (selectedToolId === CREATE_NEW_TOOL_ID) {
      if (!newToolName.trim()) return "Please enter a tool name.";
      if (!newToolAccessor.trim()) return "Please enter an accessor name.";
      if (!newToolDescription.trim()) return "Please enter a tool description.";
    }
    if (derivedDataSourceType === "CSV" && !isEdit && !csvFile) {
      return "Please upload a CSV file.";
    }
    return null;
  };

  const buildTabName = (): string => {
    return (
      userConfig.user_name +
      "_" +
      csvFile!.name.replace(/[^a-z0-9]/gi, "") +
      "_" +
      Date.now().toString()
    );
  };

  let columnsDescription: string[] = [];

  const buildColumns = (): string => {
    columnsDescription = [];
    let columns = [];
    let usedColumns = new Set<string>();

    for (let i = 0; i < csvPreview![0].length; i++) {
      const col = csvPreview![0][i];
      let c = col
        .trim()
        .replace(/\s+/g, "_")
        .replace(/[^a-zA-Z0-9_]/g, "")
        .replace(/_+/g, "_")
        .replace(/^_+|_+$/g, "")
        .toLowerCase();

      if (!c || c.length === 0) {
        c = `column_${i + 1}`;
      }
      if (/^[0-9]/.test(c)) {
        c = `col_${c}`;
      }
      if (REDSHIFT_RESERVED_KEYWORDS.has(c)) {
        c = `${c}_col`;
      }

      let finalColumnName = c;
      let counter = 1;
      while (usedColumns.has(finalColumnName)) {
        finalColumnName = `${c}_${counter}`;
        counter++;
      }

      if (!finalColumnName || !isValidRedshiftColumnName(finalColumnName)) {
        finalColumnName = `column_${i + 1}`;
        let fallbackCounter = 1;
        while (usedColumns.has(finalColumnName)) {
          finalColumnName = `column_${i + 1}_${fallbackCounter}`;
          fallbackCounter++;
        }
      }

      usedColumns.add(finalColumnName);
      columns.push(finalColumnName);
      columnsDescription.push(`${finalColumnName}: VARCHAR(65535)`);
    }
    return columns.join(",");
  };

  const handleSubmit = async () => {
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setSubmitting(true);
    try {
      let tool_id = selectedToolId!;

      // If "Create new tool" is selected (FINN admin), create the tool first
      if (selectedToolId === CREATE_NEW_TOOL_ID) {
        const addToolConfigInput: AddToolConfigInput = {
          name: newToolName,
          accessor_arn: newToolAccessor,
          is_public: true,
          description: newToolDescription,
          input_schema: JSON.stringify(newToolInputSchema),
          user_id: currentUserId,
          tool_category: newToolCategory,
          tool_type: newToolType,
        };
        const toolData = await client.graphql({
          query: addToolConfig,
          variables: { input: addToolConfigInput },
        });
        tool_id = toolData.data.addToolConfig.id;
      }

      // Handle CSV upload if needed
      let dataSourceDescription = description;
      let dataSourceAttributes = {};

      if (derivedDataSourceType === "CSV" && !isEdit && csvFile) {
        const createCsvUploadUrlInput: CreateCsvUploadUrlInput = {
          user_id: currentUserId,
          file_name: csvFile.name,
          content_type: "text/csv",
        };
        const presignedData = await client.graphql({
          query: createCsvUploadUrl,
          variables: { input: createCsvUploadUrlInput },
        });
        const upload_url = presignedData.data.createCsvUploadUrl.upload_url;
        const file_key = presignedData.data.createCsvUploadUrl.file_key;

        await fetch(upload_url, {
          method: "PUT",
          headers: { "Content-Type": "text/csv" },
          body: csvFile,
        });

        const loadCsvToRedshiftInput: LoadCsvToRedshiftInput = {
          s3_key: file_key,
          table_name: buildTabName(),
          columns: buildColumns(),
          delimiter: ",",
          has_header: true,
        };
        const loadResult = await client.graphql({
          query: loadCsvToRedshift,
          variables: { input: loadCsvToRedshiftInput },
        });
        const status = loadResult.data.loadCsvToRedshift.status;
        if (status === "FAILED") {
          throw new Error(loadResult.data.loadCsvToRedshift.message);
        }
        const tabName = loadResult.data.loadCsvToRedshift.table_name;
        const createTabDdl = loadResult.data.loadCsvToRedshift.create_table_ddl;
        dataSourceDescription =
          "DESCRIPTION: " +
          "This is a Redshift Table that was populated from a CSV file uploading.\n\n" +
          "TABLE NAME: " +
          tabName +
          "\n\n" +
          "COLUMNS: \n" +
          columnsDescription.join("\n") +
          "\n\n" +
          "TABLE DDL: " +
          createTabDdl;
      }

      // Create or update data source
      let addDataSourceInput: AddDataSourceInput;
      if (isEdit) {
        // @ts-ignore
        addDataSourceInput = {
          id: initial!.id,
          display_name: name,
          data_source_type: derivedDataSourceType,
          tool_id: tool_id,
          is_public: isPublic,
          user_id: currentUserId,
          team_id: currentTeamId,
          attributes: JSON.stringify(dataSourceAttributes),
          description: dataSourceDescription || "",
        };
      } else {
        // @ts-ignore
        addDataSourceInput = {
          display_name: name,
          data_source_type: derivedDataSourceType,
          tool_id: tool_id,
          is_public: isPublic,
          user_id: currentUserId,
          team_id: currentTeamId,
          attributes: JSON.stringify(dataSourceAttributes),
          description:
            derivedDataSourceType === "CSV" && csvFile
              ? dataSourceDescription
              : description || "",
        };
      }
      await client.graphql({
        query: addDataSource,
        variables: { input: addDataSourceInput },
      });

      setError(null);
      setSubmitting(false);
      onClose();
      reset();
    } catch (e: any) {
      console.error(e);
      setError(e.message);
      setSubmitting(false);
    }
  };

  // Build dropdown options
  const toolOptions = React.useMemo(() => {
    const opts = tools.map((t) => ({
      id: t.id,
      label: `${t.name} (ID: ${t.id})`,
    }));
    if (canCreateTool) {
      opts.unshift({ id: CREATE_NEW_TOOL_ID, label: "➕ Create new tool" });
    }
    return opts;
  }, [tools, canCreateTool]);

  const selectedOption =
    toolOptions.find((o) => o.id === selectedToolId) ?? null;

  return (
    <Dialog
      open={open}
      onClose={
        submitting
          ? undefined
          : () => {
              onClose();
              reset();
            }
      }
      maxWidth="xl"
      fullWidth
    >
      <DialogTitle>
        {isEdit ? "Edit data source" : "Add data source"}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {/* Edit mode: show ID */}
          {isEdit && initial?.id && (
            <TextField
              label="Data Source ID"
              value={initial.id}
              fullWidth
              disabled
              helperText="This ID is auto-generated and cannot be modified"
            />
          )}

          {/* Tool Selection Dropdown */}
          <Autocomplete
            options={toolOptions}
            getOptionLabel={(option) => option.label}
            value={selectedOption}
            onChange={(_, newValue) => {
              setSelectedToolId(newValue?.id ?? null);
            }}
            loading={loadingTools}
            renderOption={(props, option) => {
              const tool = tools.find((t) => t.id === option.id);
              const desc = tool?.description
                ? tool.description.substring(0, 200) +
                  (tool.description.length > 200 ? "..." : "")
                : "";
              return (
                <Tooltip title={desc} placement="right" arrow key={option.id}>
                  <li {...props}>{option.label}</li>
                </Tooltip>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Tool"
                required
                helperText="Choose the tool this data source will use"
              />
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />

          {/* Tool Description (read-only) — shown when an existing tool is selected */}
          {selectedTool && selectedToolId !== CREATE_NEW_TOOL_ID && (
            <Accordion defaultExpanded={false}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="body2" color="text.secondary">
                  Tool Description (read-only)
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: "pre-wrap",
                    maxHeight: 200,
                    overflow: "auto",
                  }}
                >
                  {selectedTool.description}
                </Typography>
              </AccordionDetails>
            </Accordion>
          )}

          {/* "Create new tool" fields — FINN admin only */}
          {selectedToolId === CREATE_NEW_TOOL_ID && canCreateTool && (
            <Stack
              spacing={2}
              sx={{
                pl: 2,
                borderLeft: "3px solid",
                borderColor: "primary.main",
              }}
            >
              <Typography variant="subtitle2" color="primary">
                New Tool Configuration
              </Typography>
              <TextField
                label="Tool name"
                value={newToolName}
                onChange={(e) => setNewToolName(e.target.value)}
                fullWidth
                required
              />
              <TextField
                label="Accessor name"
                value={newToolAccessor}
                onChange={(e) => setNewToolAccessor(e.target.value)}
                fullWidth
                required
              />
              <TextField
                label="Tool description"
                value={newToolDescription}
                onChange={(e) => setNewToolDescription(e.target.value)}
                fullWidth
                multiline
                minRows={2}
                required
              />
              <TextField
                label="Tool category"
                value={newToolCategory}
                onChange={(e) => setNewToolCategory(e.target.value)}
                fullWidth
                helperText="e.g., data, draw, utility"
              />
              <TextField
                label="Tool type"
                value={newToolType}
                onChange={(e) => setNewToolType(e.target.value)}
                fullWidth
                helperText="For data category: CSV, DB, API"
              />
              <FullSchemaEditor
                value={newToolInputSchema}
                onChange={(v) => setNewToolInputSchema(v)}
              />
            </Stack>
          )}

          {/* Data Source Name */}
          <TextField
            label="Data source name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
          />

          {/* Visibility */}
          <RadioGroup
            row
            value={isPublic ? "PUBLIC" : "PRIVATE"}
            onChange={(_, v) => setIsPublic("PUBLIC" === v)}
            aria-label="visibility"
          >
            {effectiveRole === "user" ? (
              <Tooltip
                title={"Only Admin/Operator users can make data sources public"}
              >
                <FormControlLabel
                  value="PUBLIC"
                  control={<Radio disabled />}
                  label="Public"
                />
              </Tooltip>
            ) : (
              <FormControlLabel
                value="PUBLIC"
                control={<Radio />}
                label="Public"
              />
            )}
            <FormControlLabel
              value="PRIVATE"
              control={<Radio disabled={effectiveRole === "user" && isEdit} />}
              label="Private"
            />
          </RadioGroup>

          {/* Description override */}
          <TextField
            label="Description (optional override)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            helperText="Custom description for this data source. If empty, the tool's description is used."
          />

          {/* CSV Upload — shown only when tool_type is CSV */}
          {derivedDataSourceType === "CSV" && !isEdit && (
            <Stack spacing={2}>
              <Box
                sx={{
                  p: 3,
                  border: "1px dashed",
                  borderColor: "divider",
                  borderRadius: 1,
                  textAlign: "center",
                }}
              >
                <input
                  id="csv-input"
                  type="file"
                  accept=".csv,text/csv"
                  style={{ display: "none" }}
                  onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
                />
                <label htmlFor="csv-input">
                  <Button
                    variant="outlined"
                    startIcon={<CloudUploadIcon />}
                    component="span"
                  >
                    Choose CSV file
                  </Button>
                </label>
                {csvFile && (
                  <Stack spacing={1} sx={{ mt: 2, alignItems: "center" }}>
                    <Typography variant="body2">
                      {csvFile.name} · {(csvFile.size / 1024).toFixed(1)} KB
                    </Typography>
                    {csvPreview && (
                      <Box
                        sx={{
                          maxHeight: 160,
                          overflow: "auto",
                          width: "100%",
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          Preview (first rows)
                        </Typography>
                        <Box
                          component="table"
                          sx={{
                            width: "100%",
                            borderCollapse: "collapse",
                            mt: 0.5,
                          }}
                        >
                          <tbody>
                            {csvPreview.map((row, i) => (
                              <tr key={i}>
                                {row.map((c, j) => (
                                  <td
                                    key={j}
                                    style={{
                                      border: "1px solid rgba(0,0,0,0.12)",
                                      padding: 4,
                                      fontSize: 12,
                                    }}
                                  >
                                    {c}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </Box>
                      </Box>
                    )}
                  </Stack>
                )}
              </Box>
            </Stack>
          )}

          {isEdit && derivedDataSourceType === "CSV" && (
            <Alert severity="info" variant="outlined">
              Existing CSV retained. Upload a new file by re-creating this data
              source if needed.
            </Alert>
          )}

          {error && <Alert severity="error">{error}</Alert>}
          {submitting && <LinearProgress />}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            onClose();
            reset();
          }}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={
            submitting ||
            selectedToolId === null ||
            (effectiveRole === "user" && (isPublic || isGlobal)) ||
            (isGlobal && currentTeamId !== 1)
          }
        >
          {isEdit ? "Save changes" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
